import { useId, useRef } from 'react';
import { receiptImageUrl } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import type { ReceiptSlot } from '@/features/orders/useReceiptUploads.js';
import { legacyAssetOrigin } from '@/lib/legacy-api.js';

export interface ReceiptFieldProps {
  /** The one receipt picked, if any. */
  slot?: ReceiptSlot;
  onPick: (file: File) => void;
  onRetry: () => void;
  onRemove: () => void;
  /** Set when the form was sent without one. */
  missing: boolean;
}

/**
 * The payment receipt for a China Payment request (MYP-62): one image, uploaded as soon as it is
 * picked with the same uploader as order receipts (MYP-96) — JPG or PNG up to 20 MB, shrunk before
 * it is sent — and shown uploading, uploaded with a thumbnail, or failed with a retry.
 */
export function ReceiptField({ slot, onPick, onRetry, onRemove, missing }: ReceiptFieldProps) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const errorId = useId();
  // A file refused before upload (wrong type, too big) is replaced by the next pick.
  const canPick = !slot || (slot.status === 'failed' && slot.problem !== 'failed');

  return (
    <div>
      <p className="mb-1.5 text-sm text-muted">{t('chinaPayment.form.receipt')}</p>
      {slot ? (
        <div className="flex items-center gap-2">
          {slot.status === 'uploaded' && slot.path ? (
            <span
              role="img"
              aria-label={t('chinaPayment.form.receipt')}
              style={{ backgroundImage: `url("${receiptImageUrl(slot.path, legacyAssetOrigin)}")` }}
              className="size-11 shrink-0 rounded-md border border-line bg-surface-alt bg-cover bg-center"
            />
          ) : (
            <Icon
              name={slot.status === 'uploading' ? 'hourglass_empty' : 'error'}
              size={20}
              className={slot.status === 'failed' ? 'shrink-0 text-danger' : 'shrink-0 text-muted'}
            />
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-ink">{slot.name}</span>
            <span
              role={slot.status === 'failed' ? 'alert' : undefined}
              className={cn(
                'block text-xs',
                slot.status === 'failed' ? 'text-danger' : 'text-muted',
              )}
            >
              {slot.status === 'uploading'
                ? t('orders.newOrder.receipt.uploading')
                : slot.status === 'uploaded'
                  ? t('orders.newOrder.receipt.uploaded')
                  : t(`orders.newOrder.receipt.problem.${slot.problem ?? 'failed'}`)}
            </span>
          </span>
          {slot.status === 'failed' && slot.problem === 'failed' ? (
            <button
              type="button"
              onClick={onRetry}
              className="min-h-11 rounded-md px-2 text-sm font-semibold text-brand hover:bg-brand-soft lg:min-h-8"
            >
              {t('orders.newOrder.receipt.retry')}
            </button>
          ) : null}
          {slot.status !== 'uploading' ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label={t('orders.newOrder.receipt.removeLabel', { name: slot.name })}
              className="flex size-11 items-center justify-center rounded-md text-muted hover:bg-surface-alt hover:text-ink lg:size-8"
            >
              <Icon name="close" size={16} />
            </button>
          ) : null}
        </div>
      ) : null}

      {canPick ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          aria-describedby={missing ? errorId : undefined}
          className={cn(
            'mt-2 inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-md border px-4',
            'text-sm font-semibold text-brand transition-colors hover:bg-brand-soft lg:min-h-10',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
            missing ? 'border-danger' : 'border-brand/40',
          )}
        >
          <Icon name="upload" size={17} />
          {t('chinaPayment.form.uploadReceipt')}
        </button>
      ) : null}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png"
        aria-label={t('chinaPayment.form.receipt')}
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onPick(file);
          // Cleared so picking the same file twice still fires a change event.
          event.target.value = '';
        }}
      />
      {missing ? (
        <p id={errorId} className="mt-1.5 text-xs text-danger">
          {t('chinaPayment.form.problem.receipt')}
        </p>
      ) : (
        <p className="mt-1.5 text-xs text-muted">{t('chinaPayment.form.receiptHint')}</p>
      )}
    </div>
  );
}
